<script setup>
  import { computed, onMounted, nextTick, ref, useTemplateRef, watch } from 'vue';
  import { useMediaQuery } from '@vueuse/core'

  const activeIndex = ref(0)
  const activeBodyHeight = ref(0)
  const wrapper = useTemplateRef('wrapper')

  const isLargeScreen = useMediaQuery('(min-width: 1024px)')

  const props = defineProps({
    headline: String,
    items: String
  })

  const items = computed(() => {
    return JSON.parse(props.items)
  })

  onMounted(() => {
    setBodyHeight()
  })

  watch(activeIndex, () => {
    if (!wrapper.value) return false
    nextTick(() => {
      setBodyHeight()
    })
  })

  const setBodyHeight = () => {
    const activeItem = document.querySelector('.fade-list-item.is-active')
    if (!activeItem) {
      activeBodyHeight.value = wrapper.value.offsetHeight
      return
    }
    if (isLargeScreen.value) {
      activeBodyHeight.value = activeItem.offsetHeight
      return
    }
    activeBodyHeight.value = activeItem.offsetHeight + 400
  }
</script>

<template>
  <div
    ref="wrapper"
    class="fade-list flex flex-col lg:flex-row gap-8 lg:gap-20 max-w-screen-2xl mx-auto px-6"
    :style="`min-height: ${ activeBodyHeight }px`"
  >
    <div
      class="w-full lg:w-1/2 xl:pr-20"
    >
      <p
        v-if="headline"
        class="uppercase ~text-xs/sm tracking-wider"
      >
        {{ headline }}
      </p>
      <div
        class="fade-list-navigation"
      >
        <button
          v-for="(item, index) in items"
          :key="index"
          @click="activeIndex = index"
          class="fade-list-navigation-item w-full flex flex-row items-center justify-start relative opacity-60 hover:opacity-100 transition-all duration-500 py-6"
        >
          <span
            class="fade-list-navigation-item-icon overflow-hidden"
            :class="activeIndex === index ? 'is-active' : ''"
          >
            <AppIcon
              name="ArrowLink"
            />
          </span>
          <span
            class="~text-2xl/3xl text-headline text-left ml-3"
          >
            {{ item.headline }}
          </span>
        </button>
      </div>
    </div>
    <div
      class="fade-list-items relative w-full lg:w-1/2"
    >
      <div
        v-for="item, index in items"
        :key="index"
        class="fade-list-item absolute transition-all duration-500 top-0 w-full"
        :class="activeIndex === index ? 'is-active opacity-100 left-0' : 'opacity-0 left-8'"
        v-html="item.text"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .fade-list {
    @apply transition-all duration-300 ease-in-out;
  }
  .fade-list-navigation-item {
    :deep(svg) {
      @apply mr-3;
    }
    :deep(svg path) {
      fill: #3D1314 !important;
    }
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 100%;
      opacity: 60%;
      background: #3D1314;
    }
    &::after {
      content: '';
      position: absolute;
      transition: width 0.5s ease-in-out;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 0;
      background: #3D1314;
    }
    &:hover {
      &::after {
        width: 100%;
      }
    }
  }
  .fade-list-item {
    :deep(h2) {
      @apply ~text-xl/2xl ~mb-4/6;
    }
    :deep(h3) {
      @apply ~text-lg/xl ~mb-4/6;
    }
    :deep(p) {
      @apply ~my-4/6;
    }

    :deep(ul li) {
      @apply ~text-sm/base py-1;
      &::before {
        @apply top-3.5 h-1 w-1;
      }
    }
  }
  .fade-list-navigation-item-icon {
    @apply w-0 opacity-0;
    transition: width 0.3s ease-in-out 0.3s, opacity 0.3s ease-in-out;
    &.is-active {
      @apply w-8 opacity-100;
      transition: width 0.3s ease-in-out, opacity 0.3s ease-in-out 0.3s;
    }

  }

  .fade-right-enter-active,
  .fade-right-leave-active {
    transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
  }

  .fade-right-enter-from {
    opacity: 0;
    transform: translateX(40px);
  }

  .fade-right-leave-to {
    opacity: 0;
    transform: translateX(40px);
  }

  .fade-right-enter-to,
  .fade-right-leave-from {
    opacity: 1;
    transform: translateX(0);
  }
</style>
